import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { SectionCap, EditorialButton } from './Editorial';

/* "Your player card" — the flipping AI card GIF the nightly autofill run makes.
   Renders nothing while loading, on a 404 (no GIF yet) or on any error, so an
   athlete without a GIF sees no change to the page. When the API returns a
   `back_url`, the server-drawn back image is shown beside the GIF (Download and
   Share stay GIF-only). `onState` (optional) is told 'loading', then 'ready'
   or 'none', so a host page can say when there is no card yet. */

const FILE_NAME = 'prozpects-player-card.gif';
const GIF_TYPE = 'image/gif';
const SHARE_TITLE = 'My ProZpects player card';
const SHARE_FAILED = "Couldn't share. Try Download instead.";
// Safari and Firefox can cancel a blob download whose URL is revoked too soon.
export const REVOKE_DELAY_MS = 10000;

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const gifFile = (blob) => new File([blob], FILE_NAME, { type: GIF_TYPE });

// Can this browser share a GIF file? Probed with an empty GIF-typed File.
function canShareGif() {
  try {
    return typeof navigator !== 'undefined'
      && typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [gifFile(new Blob([], { type: GIF_TYPE }))] }) === true;
  } catch (e) {
    return false;
  }
}

// The presigned S3 URL is cross-origin. The bucket's CORS allows GET from the
// production origin; `no-store` keeps the browser from answering this CORS
// request with the <img>'s cached non-CORS response (no Allow-Origin header).
async function fetchGif(url) {
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!res.ok) throw new Error(`gif fetch ${res.status}`);
  return res.blob();
}

function clickLink(href, newTab) {
  const a = document.createElement('a');
  a.setAttribute('href', href);
  a.setAttribute('download', FILE_NAME);
  if (newTab) {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener');
  }
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const CARD_IMG_STYLE = { display: 'block', width: '100%', maxWidth: 320, height: 'auto', aspectRatio: '5 / 7' };

export default function CardGifPanel({ onState } = {}) {
  const { API_URL } = useAuth();
  // Held in a ref so a host passing an inline callback doesn't refetch.
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const [gif, setGif] = useState(null);
  const [shareable, setShareable] = useState(false);
  const [error, setError] = useState('');
  const blobRef = useRef(null);

  useEffect(() => {
    let live = true;
    const report = (state) => { if (live && onStateRef.current) onStateRef.current(state); };
    report('loading');
    axios.get(`${API_URL}/player-cards/me/flip-gif`, authConfig())
      .then(({ data }) => {
        if (!live) return;
        if (!data || !data.gif_url) { report('none'); return; }
        setGif(data);
        report('ready');
        if (canShareGif()) {
          setShareable(true);
          // Fetch ahead so the share sheet opens straight from the tap: Safari
          // drops the tap's user activation if a long fetch sits in between.
          fetchGif(data.gif_url)
            .then((blob) => { if (live) blobRef.current = blob; })
            .catch(() => {});
        }
      })
      .catch(() => { report('none'); /* 404 or any error: render nothing */ });
    return () => { live = false; };
  }, [API_URL]);

  if (!gif) return null;

  const getBlob = async () => {
    if (!blobRef.current) blobRef.current = await fetchGif(gif.gif_url);
    return blobRef.current;
  };

  const handleDownload = async () => {
    setError('');
    let blob;
    try {
      blob = await getBlob();
    } catch (e) {
      // Fetch blocked (e.g. an origin the bucket's CORS doesn't list): open the
      // GIF itself so the user can save it.
      clickLink(gif.gif_url, true);
      return;
    }
    const href = URL.createObjectURL(blob);
    clickLink(href, false);
    setTimeout(() => URL.revokeObjectURL(href), REVOKE_DELAY_MS);
  };

  const handleShare = async () => {
    setError('');
    try {
      const files = [gifFile(await getBlob())];
      if (!navigator.canShare?.({ files })) throw new Error('cannot share');
      await navigator.share({ files, title: SHARE_TITLE });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setError(SHARE_FAILED);
    }
  };

  return (
    <>
      <SectionCap label="Your player card" hint="DOWNLOAD OR SHARE" />
      <div className="ed-card">
        <div className="ed-row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <img
            src={gif.gif_url}
            alt="Your ProZpects player card, flipping"
            width={720}
            height={1008}
            style={CARD_IMG_STYLE}
          />
          {gif.back_url && (
            <img
              src={gif.back_url}
              alt="Back of your player card"
              width={720}
              height={1008}
              style={CARD_IMG_STYLE}
            />
          )}
        </div>
        <div className="ed-row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <EditorialButton variant="light" onClick={handleDownload}>Download</EditorialButton>
          {shareable && (
            <EditorialButton variant="ghost" onClick={handleShare}>Share</EditorialButton>
          )}
        </div>
        {error && <p role="alert" className="ed-field__hint" style={{ marginTop: 8 }}>{error}</p>}
      </div>
    </>
  );
}
